import { useCallback, useEffect, useState } from 'react';
import {
  getBroadcastMentionTrackingSettings,
  patchBroadcastMentionTrackingSettings,
} from '../api/client';

interface BroadcastMentionTrackingToggleProps {
  teamId: string;
  /** When false, toggle and Apply are read-only (default true for staff). */
  canEdit?: boolean;
  /** When set, skip GET and use this as the last known server value. */
  initialEnabled?: boolean;
}

/**
 * Staff toggle for broadcast mention tracking. Toggle updates local pending state;
 * PATCH runs on Apply. PATCH response is source of truth.
 */
export function BroadcastMentionTrackingToggle({
  teamId,
  canEdit = true,
  initialEnabled,
}: BroadcastMentionTrackingToggleProps) {
  const skipLoad = initialEnabled !== undefined;
  const [serverEnabled, setServerEnabled] = useState<boolean | null>(
    skipLoad ? initialEnabled : null
  );
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(
    skipLoad ? initialEnabled : null
  );
  const [loading, setLoading] = useState(!skipLoad);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [patchError, setPatchError] = useState<string | null>(null);
  const [patching, setPatching] = useState(false);

  const hasPendingChange =
    serverEnabled !== null && pendingEnabled !== null && pendingEnabled !== serverEnabled;
  const applyDisabled = !canEdit || !hasPendingChange || patching;
  const controlsDisabled = !canEdit || patching;

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const settings = await getBroadcastMentionTrackingSettings(teamId);
      setServerEnabled(settings.enabled);
      setPendingEnabled(settings.enabled);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load broadcast mention tracking');
      setServerEnabled(null);
      setPendingEnabled(null);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (skipLoad) return;
    void loadSettings();
  }, [loadSettings, skipLoad]);

  function handleToggleChange(nextEnabled: boolean) {
    if (pendingEnabled === null || controlsDisabled) return;
    setPatchError(null);
    setPendingEnabled(nextEnabled);
  }

  async function handleApplyClick() {
    if (applyDisabled || pendingEnabled === null || serverEnabled === null || patching) return;
    setPatchError(null);
    setPatching(true);
    try {
      const settings = await patchBroadcastMentionTrackingSettings(teamId, {
        enabled: pendingEnabled,
      });
      setServerEnabled(settings.enabled);
      setPendingEnabled(settings.enabled);
    } catch (e) {
      setPendingEnabled(serverEnabled);
      setPatchError(e instanceof Error ? e.message : 'Failed to update broadcast mention tracking');
    } finally {
      setPatching(false);
    }
  }

  if (loading) {
    return (
      <p style={styles.muted} data-testid="broadcast-mention-tracking-loading">
        Loading broadcast mention tracking…
      </p>
    );
  }

  if (loadError) {
    return (
      <div data-testid="broadcast-mention-tracking-load-error">
        <p style={styles.error}>{loadError}</p>
        <button type="button" onClick={() => void loadSettings()} style={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div data-testid="broadcast-mention-tracking-section">
      <label style={styles.toggleLabel}>
        <input
          type="checkbox"
          checked={pendingEnabled ?? false}
          disabled={controlsDisabled}
          onChange={(e) => handleToggleChange(e.target.checked)}
          data-testid="broadcast-mention-tracking-toggle"
        />
        <span>Track @here and @channel mentions</span>
      </label>
      <p style={styles.helper}>
        When enabled, tracking a message with @here or @channel expands those mentions to channel
        members as ack targets. This can increase target counts and track credit usage.
      </p>
      <div style={styles.actions}>
        <button
          type="button"
          onClick={() => void handleApplyClick()}
          disabled={applyDisabled}
          style={applyDisabled ? styles.applyButtonDisabled : styles.applyButton}
          data-testid="broadcast-mention-tracking-apply"
        >
          {patching ? 'Applying…' : 'Apply'}
        </button>
      </div>
      {patchError && (
        <p style={styles.error} data-testid="broadcast-mention-tracking-patch-error">
          {patchError}
        </p>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  muted: {
    margin: 0,
    color: '#6b7280',
    fontSize: 14,
  },
  error: {
    margin: '8px 0 0',
    color: '#b91c1c',
    fontSize: 14,
  },
  retryButton: {
    marginTop: 8,
    padding: '6px 12px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    background: '#fff',
    cursor: 'pointer',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
    cursor: 'pointer',
  },
  helper: {
    margin: '8px 0 0 28px',
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 1.5,
  },
  actions: {
    margin: '12px 0 0 28px',
  },
  applyButton: {
    padding: '8px 14px',
    fontSize: 14,
    fontWeight: 500,
    color: '#fff',
    background: '#2563eb',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  applyButtonDisabled: {
    padding: '8px 14px',
    fontSize: 14,
    fontWeight: 500,
    color: '#9ca3af',
    background: '#e5e7eb',
    border: 'none',
    borderRadius: 6,
    cursor: 'not-allowed',
  },
};
